import { ApplicationState } from '../../store/application-store';
import { Injectable } from '@angular/core';
import { Observable, empty, combineLatest } from 'rxjs';
import { tap, filter, map, distinctUntilChanged, catchError, switchMap } from 'rxjs';
import { select, Store } from '@ngrx/store';
import * as userProfileSelectors from '../selectors/user-profiles';
import { UserProfile } from '../models/user-profile';
import * as userProfileActions from '../actions/user-profiles';
import { UserProfilesHttpService } from './user-profiles-http.service';
import { Profile } from '../../profiles/models/profile';
import { SetUserProfileModel } from '../models/set-user-profile.model';
import { ToastrService } from 'ngx-toastr';

@Injectable()
export class UserProfilesService {

  constructor(
    private readonly store: Store<ApplicationState>,
    private readonly userProfilesHttpService: UserProfilesHttpService,
    private readonly messageService: ToastrService
  ) { }

  private getUserProfilesFromApi(): Observable<UserProfile[]> {
     this.store.dispatch(new userProfileActions.Load());
     return this.userProfilesHttpService.getAllUserProfiles().pipe(
      tap((users: UserProfile[]) => {
        this.store.dispatch(new userProfileActions.LoadComplete(users));
      }),
      catchError(err => {
        this.messageService.error('An error occurred whilst trying to retrieve the profiles', 'Error', { disableTimeOut: true });
        return [];
      })
    );
  }

  getUserProfiles(): Observable<UserProfile[]> {
    const combined = combineLatest(this.store.select(userProfileSelectors.selectUserProfileState),
      this.getUserProfilesFromApi());
    return combined.pipe(
        filter(([state, userProfiles]) => (!state.loading && state.userProfiles !== undefined)),
        map(([state, userProfiles]) => state.userProfiles),
        distinctUntilChanged()
      );
  }

  setUserProfiles(userProfiles: UserProfile[], profile: Profile): Observable<object> {
    const setUserProfileModel: SetUserProfileModel = {
      UserIdsToUpdate: userProfiles.map(x => x.SystemUserId),
      ProfileId: profile.id
    }

    return this.userProfilesHttpService.setUserProfiles(setUserProfileModel).pipe(
      tap(() => {
        this.messageService.success(`${userProfiles.length} ${userProfiles.length > 1 ? 'profiles have' : 'profile has'} been updated`, 'Profile(s) updated');
      }),
      switchMap(() => this.getUserProfilesFromApi()),
      catchError(() => {
        this.messageService.error(`Profiles failed to update`, 'Update Error');
        return empty();
      }));
  }

  getLoading(): Observable<boolean> {
    return this.store
      .pipe(
      select(userProfileSelectors.selectUserProfilesLoading),
        filter((loading: boolean) => loading !== undefined),
        map((loading: boolean) => loading),
        distinctUntilChanged()
      );
  }
}
